import type { BetterAuthClientPlugin } from "better-auth"
import { createAuthClient } from "better-auth/react"

export const taktWebAppIds = ["admin", "merchant", "web"] as const
export type TaktWebAppId = (typeof taktWebAppIds)[number]

export type AuthContinuation = {
    app: TaktWebAppId
    returnTo: string
}

export const taktIdentityAccountPages = ["overview", "profile", "security", "sessions"] as const
export type TaktIdentityAccountPage = (typeof taktIdentityAccountPages)[number]

export type ContinuationResolution =
    | { continuation: AuthContinuation; destination: string; label: string; valid: true }
    | { continuation: null; destination: null; label: null; valid: false }

export type WebAuthSessionUser = {
    id?: string
    email?: string | null
    name?: string | null
    image?: string | null
    avatar?: string | null
    role?: string | null
    emailVerified?: boolean
}

export type WebAuthSessionRecord = {
    id?: string
    token?: string | null
}

const appIdSet = new Set<TaktWebAppId>(taktWebAppIds)
const MAX_RETURN_TO_LENGTH = 2048
type UnknownRecord = Record<string, unknown>

const isRecord = (value: unknown): value is UnknownRecord => typeof value === "object" && value !== null

function hasUnsafeCharacters(value: string): boolean {
    return [...value].some((character) => {
        const codePoint = character.codePointAt(0) ?? 0
        return character === "\\" || codePoint <= 31 || codePoint === 127
    })
}

export function isTaktWebAppId(value: unknown): value is TaktWebAppId {
    return typeof value === "string" && appIdSet.has(value as TaktWebAppId)
}

export function isSafeLocalReturnPath(value: unknown): value is string {
    if (
        typeof value !== "string" ||
        value.length === 0 ||
        value.length > MAX_RETURN_TO_LENGTH ||
        !value.startsWith("/") ||
        value.startsWith("//")
    ) {
        return false
    }

    let decoded = value
    for (let attempt = 0; attempt < 3; attempt += 1) {
        if (hasUnsafeCharacters(decoded) || decoded.startsWith("//")) return false
        try {
            const next = decodeURIComponent(decoded)
            if (next === decoded) break
            decoded = next
        } catch {
            return false
        }
    }

    return !hasUnsafeCharacters(decoded) && !decoded.startsWith("//")
}

export function resolveAuthContinuation(input: {
    app: unknown
    returnTo: unknown
    appOrigins: Readonly<Record<TaktWebAppId, string>>
    appLabels: Readonly<Record<TaktWebAppId, string>>
}): ContinuationResolution {
    if (!isTaktWebAppId(input.app) || !isSafeLocalReturnPath(input.returnTo)) {
        return { continuation: null, destination: null, label: null, valid: false }
    }

    try {
        const origin = new URL(input.appOrigins[input.app])
        const destination = new URL(input.returnTo, origin)
        if (destination.origin !== origin.origin || destination.username || destination.password) {
            return { continuation: null, destination: null, label: null, valid: false }
        }

        return {
            continuation: {
                app: input.app,
                returnTo: `${destination.pathname}${destination.search}${destination.hash}`,
            },
            destination: destination.toString(),
            label: input.appLabels[input.app],
            valid: true,
        }
    } catch {
        return { continuation: null, destination: null, label: null, valid: false }
    }
}

export function continuationSearch(continuation: AuthContinuation | null): Record<string, string> {
    return continuation ? { app: continuation.app, returnTo: continuation.returnTo } : {}
}

export function buildIdentityUrl(input: {
    identityOrigin: string
    path: string
    continuation?: AuthContinuation | null
}): string {
    if (!isSafeLocalReturnPath(input.path)) {
        throw new TypeError("Identity paths must be local")
    }

    const identityOrigin = new URL(input.identityOrigin)
    const url = new URL(input.path, identityOrigin)
    if (url.origin !== identityOrigin.origin || url.username || url.password) {
        throw new TypeError("Identity paths must stay on the configured origin")
    }

    if (input.continuation) {
        url.searchParams.set("app", input.continuation.app)
        url.searchParams.set("returnTo", input.continuation.returnTo)
    }
    return url.toString()
}

export function buildIdentitySignInUrl(input: {
    identityOrigin: string
    continuation?: AuthContinuation | null
}): string {
    return buildIdentityUrl({ ...input, path: "/sign-in" })
}

const identityAccountPaths: Record<TaktIdentityAccountPage, string> = {
    overview: "/",
    profile: "/profile",
    security: "/security",
    sessions: "/sessions",
}

export function buildIdentityAccountUrl(input: {
    identityOrigin: string
    page?: TaktIdentityAccountPage
    continuation?: AuthContinuation | null
}): string {
    return buildIdentityUrl({ ...input, path: identityAccountPaths[input.page ?? "overview"] })
}

export function createTaktWebAuthClient<const Plugins extends BetterAuthClientPlugin[] = []>(
    authBaseUrl: string,
    plugins: Plugins = [] as unknown as Plugins,
) {
    return createAuthClient({
        baseURL: authBaseUrl.replace(/\/+$/, ""),
        fetchOptions: { credentials: "include" },
        plugins,
    })
}

export function getWebAuthSessionUser(sessionData: unknown): WebAuthSessionUser | null {
    if (!isRecord(sessionData)) return null
    const nestedSession = isRecord(sessionData.session) ? sessionData.session : null
    const rawUser = sessionData.user ?? (nestedSession && isRecord(nestedSession.user) ? nestedSession.user : null)
    if (!isRecord(rawUser)) return null

    return {
        id: typeof rawUser.id === "string" ? rawUser.id : undefined,
        email: typeof rawUser.email === "string" ? rawUser.email : null,
        name: typeof rawUser.name === "string" ? rawUser.name : null,
        image: typeof rawUser.image === "string" ? rawUser.image : null,
        avatar: typeof rawUser.avatar === "string" ? rawUser.avatar : null,
        role: typeof rawUser.role === "string" ? rawUser.role : null,
        emailVerified: rawUser.emailVerified === true,
    }
}

export function getWebAuthSessionRecord(sessionData: unknown): WebAuthSessionRecord | null {
    if (!isRecord(sessionData) || !isRecord(sessionData.session)) return null
    return {
        id: typeof sessionData.session.id === "string" ? sessionData.session.id : undefined,
        token: typeof sessionData.session.token === "string" ? sessionData.session.token : null,
    }
}

export const isAdminWebAuthSession = (sessionData: unknown): boolean =>
    getWebAuthSessionUser(sessionData)?.role === "admin"
